/**
 * Notification Hidden State Service
 * Manages persistent localStorage for hidden/dismissed notifications
 * Best practice: Keep hiding behavior local but persistent across page refreshes
 */

const STORAGE_KEY = 'botblender_hidden_notifications';
const MAX_HIDDEN_IDS = 1000; // Prevent localStorage from growing too large
const CLEANUP_DAYS = 30; // Remove hidden IDs older than 30 days

interface HiddenNotificationEntry {
  id: string;
  hiddenAt: number; // timestamp
  reason: 'dismissed' | 'hidden';
}

interface HiddenNotificationStorage {
  entries: HiddenNotificationEntry[];
  lastCleanup: number;
}

class NotificationHiddenStateService {
  /**
   * Get all hidden notification IDs
   */
  getHiddenIds(): Set<string> {
    try {
      const data = this.getStorageData();
      return new Set(data.entries.map(entry => entry.id));
    } catch (error) {
      console.warn('Failed to get hidden notification IDs:', error);
      return new Set();
    }
  }

  /**
   * Mark a notification as hidden/dismissed
   */
  hideNotification(id: string, reason: 'dismissed' | 'hidden' = 'dismissed'): void {
    try {
      const data = this.getStorageData();
      
      // Remove existing entry if it exists
      data.entries = data.entries.filter(entry => entry.id !== id);
      
      // Add new entry
      data.entries.push({
        id,
        hiddenAt: Date.now(),
        reason
      });

      // Trim if too many entries
      if (data.entries.length > MAX_HIDDEN_IDS) {
        // Keep most recent entries
        data.entries.sort((a, b) => b.hiddenAt - a.hiddenAt);
        data.entries = data.entries.slice(0, MAX_HIDDEN_IDS);
      }

      this.saveStorageData(data);
      
      console.log(`📝 [Hidden State] Notification ${id} marked as ${reason}`);
    } catch (error) {
      console.error('Failed to hide notification:', error);
    }
  }

  /**
   * Mark multiple notifications as hidden
   */
  hideMultipleNotifications(ids: string[], reason: 'dismissed' | 'hidden' = 'hidden'): void {
    try {
      const data = this.getStorageData();
      const timestamp = Date.now();
      
      // Remove existing entries for these IDs
      const existingIds = new Set(ids);
      data.entries = data.entries.filter(entry => !existingIds.has(entry.id));
      
      // Add new entries
      const newEntries = ids.map(id => ({
        id,
        hiddenAt: timestamp,
        reason
      }));
      
      data.entries.push(...newEntries);

      // Trim if too many entries
      if (data.entries.length > MAX_HIDDEN_IDS) {
        data.entries.sort((a, b) => b.hiddenAt - a.hiddenAt);
        data.entries = data.entries.slice(0, MAX_HIDDEN_IDS);
      }

      this.saveStorageData(data);
      
      console.log(`📝 [Hidden State] ${ids.length} notifications marked as ${reason}`);
    } catch (error) {
      console.error('Failed to hide multiple notifications:', error);
    }
  }

  /**
   * Check if a notification is hidden
   */
  isHidden(id: string): boolean {
    try {
      const hiddenIds = this.getHiddenIds();
      return hiddenIds.has(id);
    } catch (error) {
      console.warn('Failed to check if notification is hidden:', error);
      return false;
    }
  }

  /**
   * Un-hide a notification (restore it)
   */
  restoreNotification(id: string): void {
    try {
      const data = this.getStorageData();
      data.entries = data.entries.filter(entry => entry.id !== id);
      this.saveStorageData(data);
      
      console.log(`🔄 [Hidden State] Notification ${id} restored`);
    } catch (error) {
      console.error('Failed to restore notification:', error);
    }
  }

  /**
   * Clean up old hidden notification IDs
   */
  cleanup(): void {
    try {
      const data = this.getStorageData();
      const cutoff = Date.now() - (CLEANUP_DAYS * 24 * 60 * 60 * 1000);
      
      const beforeCount = data.entries.length;
      data.entries = data.entries.filter(entry => entry.hiddenAt > cutoff);
      const afterCount = data.entries.length;
      
      data.lastCleanup = Date.now();
      this.saveStorageData(data);
      
      if (beforeCount > afterCount) {
        console.log(`🧹 [Hidden State] Cleaned up ${beforeCount - afterCount} old hidden notifications`);
      }
    } catch (error) {
      console.error('Failed to cleanup hidden notifications:', error);
    }
  }

  /**
   * Get statistics about hidden notifications
   */
  getStats(): { total: number, dismissed: number, hidden: number, oldestEntry: number | null } {
    try {
      const data = this.getStorageData();
      const dismissed = data.entries.filter(e => e.reason === 'dismissed').length;
      const hidden = data.entries.filter(e => e.reason === 'hidden').length;
      const oldestEntry = data.entries.length > 0 
        ? Math.min(...data.entries.map(e => e.hiddenAt))
        : null;
      
      return {
        total: data.entries.length,
        dismissed,
        hidden,
        oldestEntry
      };
    } catch (error) {
      console.warn('Failed to get hidden notification stats:', error);
      return { total: 0, dismissed: 0, hidden: 0, oldestEntry: null };
    }
  }

  /**
   * Clear all hidden notifications (for testing/debugging)
   */
  clearAll(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
      console.log('🗑️ [Hidden State] All hidden notifications cleared');
    } catch (error) {
      console.error('Failed to clear hidden notifications:', error);
    }
  }

  /**
   * Get raw storage data
   */
  private getStorageData(): HiddenNotificationStorage {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return { entries: [], lastCleanup: Date.now() };
      }

      const parsed = JSON.parse(stored) as HiddenNotificationStorage;
      
      // Validate structure
      if (!Array.isArray(parsed.entries)) {
        throw new Error('Invalid storage structure');
      }

      // Auto cleanup if it's been a while
      const shouldCleanup = !parsed.lastCleanup || 
        (Date.now() - parsed.lastCleanup) > (7 * 24 * 60 * 60 * 1000); // 7 days
      
      if (shouldCleanup) {
        // Will be cleaned up when saved
        return { ...parsed, lastCleanup: parsed.lastCleanup || 0 };
      }

      return parsed;
    } catch (error) {
      console.warn('Failed to parse hidden notification storage, resetting:', error);
      return { entries: [], lastCleanup: Date.now() };
    }
  }

  /**
   * Save storage data
   */
  private saveStorageData(data: HiddenNotificationStorage): void {
    try {
      // Auto cleanup if needed
      if (!data.lastCleanup || (Date.now() - data.lastCleanup) > (7 * 24 * 60 * 60 * 1000)) {
        const cutoff = Date.now() - (CLEANUP_DAYS * 24 * 60 * 60 * 1000);
        data.entries = data.entries.filter(entry => entry.hiddenAt > cutoff);
        data.lastCleanup = Date.now();
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save hidden notification storage:', error);
      
      // If localStorage is full, try to clear old entries and retry
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        try {
          // Aggressive cleanup - keep only last 100 entries
          data.entries.sort((a, b) => b.hiddenAt - a.hiddenAt);
          data.entries = data.entries.slice(0, 100);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
          console.log('🧹 [Hidden State] Recovered from quota exceeded by aggressive cleanup');
        } catch (retryError) {
          console.error('Failed to save even after cleanup:', retryError);
        }
      }
    }
  }
}

// Export singleton instance
export const notificationHiddenState = new NotificationHiddenStateService();

// Auto cleanup on service creation
notificationHiddenState.cleanup();